/**
 * TimeSelectField - 오전/오후 + 12시간제 시(1~12) + 분(10분 단위) 분리 native 드롭다운으로
 * "HH:mm"(24시간제)을 입력받는다. 모바일에서 OS 기본 피커를 그대로 활용하기 위해
 * native <select>를 사용하며, 외부로는 항상 24시간제 "HH:mm"을 주고받는다.
 */

import {
  PERIODS,
  HOUR12_OPTIONS,
  MINUTE_OPTIONS,
  to12Hour,
  to24Hour,
  type Period,
} from "@/utils/calendar";

interface TimeSelectFieldProps {
  /** "HH:mm" (24시간제) */
  value: string;
  onChange: (next: string) => void;
  /** 각 select의 aria-label 접두사 (예: "시작"). */
  label?: string;
}

const SELECT_CLASS =
  "px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-primary-400";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function TimeSelectField({ value, onChange, label }: TimeSelectFieldProps) {
  const [hourStr = "", minuteStr = ""] = value.split(":");
  const hour24 = Number(hourStr);
  const minute = Number(minuteStr);
  const { period, hour12 } = to12Hour(Number.isNaN(hour24) ? 0 : hour24);
  const safeMinute = Number.isNaN(minute) ? 0 : minute;

  const emit = (nextPeriod: Period, nextHour12: number, nextMinute: number) =>
    onChange(`${pad2(to24Hour(nextPeriod, nextHour12))}:${pad2(nextMinute)}`);

  return (
    <div className="flex items-center gap-1">
      <select
        aria-label={label ? `${label} 오전/오후` : "오전/오후"}
        value={period}
        onChange={(e) => emit(e.target.value as Period, hour12, safeMinute)}
        className={SELECT_CLASS}
      >
        {PERIODS.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
      <select
        aria-label={label ? `${label} 시` : "시"}
        value={hour12}
        onChange={(e) => emit(period, Number(e.target.value), safeMinute)}
        className={SELECT_CLASS}
      >
        {HOUR12_OPTIONS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-gray-500">:</span>
      <select
        aria-label={label ? `${label} 분` : "분"}
        value={safeMinute}
        onChange={(e) => emit(period, hour12, Number(e.target.value))}
        className={SELECT_CLASS}
      >
        {MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {pad2(m)}
          </option>
        ))}
      </select>
    </div>
  );
}
